/**
 * LiveNotificationToaster
 *
 * Headless, app-shell-level component that raises a toast for each
 * notification arriving over the socket while the app is on screen — the
 * in-app half of "you have a notification"; `useNotifications` raises an OS
 * notification for the half where it is not. Renders nothing; mount once
 * inside the authenticated tree, within ToastProvider, EchoProvider and
 * PaywallProvider (a Generate can be refused by the paywall).
 */

import { useCallback, useEffect } from 'react';

import {
  JOB_PROGRESS_CONSTANTS,
  NotificationUtils,
} from '@personal-trainer-app/shared';

import type { RawNotification } from '@personal-trainer-app/shared';

import { useEchoContext } from '@/components/providers/EchoProvider';
import { useToast } from '@/components/ui/Toast';
import {
  useNotificationActions,
  useNotificationCta,
} from '@/hooks/notifications';
import { NotificationToast } from '@/layouts/components/NotificationToast';

/** A question, a failure or a celebration is given longer to be read. */
const DURATION_MS = { standard: 6000, extended: 10000 } as const;

/**
 * Kinds the job toaster already announces — it watches the export job itself
 * and offers the same Download, so a second toast would only repeat it.
 */
const ANNOUNCED_BY_JOB_TOASTER = new Set([
  'report_exported',
  'report_export_failed',
]);

export function LiveNotificationToaster(): null {
  const { subscribeToNotifications } = useEchoContext();
  const { addToast } = useToast();
  const { markAsRead, resolveNotification } = useNotificationActions();

  const onRead = useCallback(
    (id: string) => void markAsRead(id).catch(() => undefined),
    [markAsRead]
  );
  const onResolved = useCallback(
    (id: string) => void resolveNotification(id),
    [resolveNotification]
  );
  const { run } = useNotificationCta({ onRead, onResolved });

  useEffect(
    () =>
      subscribeToNotifications((notification: RawNotification) => {
        // Off screen, the OS notification speaks instead.
        if (document.visibilityState !== 'visible') return;

        if (
          JOB_PROGRESS_CONSTANTS.LIVE_JOB_PROGRESS &&
          ANNOUNCED_BY_JOB_TOASTER.has(NotificationUtils.getKind(notification))
        ) {
          return;
        }

        const priority = NotificationUtils.getPriority(notification);

        addToast({
          duration:
            priority === 'low' || priority === 'medium'
              ? DURATION_MS.standard
              : DURATION_MS.extended,
          render: ({ remove, duration }) => (
            <NotificationToast
              notification={notification}
              duration={duration}
              onAction={run}
              onRemove={remove}
            />
          ),
        });
      }),
    [subscribeToNotifications, addToast, run]
  );

  return null;
}

export default LiveNotificationToaster;
